import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash/fp';
import {
  Modal,
  Button,
  Icon,
  Form,
  FormGroup,
  ControlLabel,
  FormControl,
  Schema
} from 'rsuite';
import styled from 'styled-components';
import { updateInventoryTag } from '../../../redux/Inventory/sagas/tag/routines';
import { fetchInventoryTagItemCount } from '../../../redux/Inventory/sagas/components/edit-tag-modal/routines';
import {
  getIsSaving,
  getIsSaved
} from '../../../redux/Inventory/selectors/tag';
import { getTagItemCount } from '../../../redux/Inventory/selectors/components/edit-tag-model';

const StyledIcon = styled(Icon)`
  color: #ffb300;
  font-size: 18px;
  margin-right: 5px;
`;

const { StringType } = Schema.Types;

const FormModel = Schema.Model({
  name: StringType().isRequired('This field is required')
});

function EditTagmodal({ show, onClose, tag }) {
  const dispatch = useDispatch();
  const formRef = useRef();

  const [formValue, setFormValue] = useState();
  const [formError, setFormError] = useState({});
  const hasTagItems = useSelector(getTagItemCount);
  const isSaving = useSelector(getIsSaving);
  const isSaved = useSelector(getIsSaved);
  const allowSubmit = isEmpty(formError);

  const toastrConfirmOptions = {
    onOk: () => {
      dispatch(updateInventoryTag(formValue));
    },
    onCancel: () => null,
    okText: 'Proceed Saving',
    cancelText: 'Cancel'
  };

  useEffect(
    () => {
      const tagId = get('id', tag);
      dispatch(fetchInventoryTagItemCount(tagId));
      setFormValue(tag);
      setFormError({});
    },
    [tag]
  );

  useEffect(
    () => {
      if (isSaved) {
        onClose();
      }
    },
    [isSaved]
  );

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = error => {
    setFormError(error);
  };

  const handleFormSubmit = () => {
    const noError = formRef.current.check();
    if (noError) {
      if (hasTagItems) {
        toastr.confirm(
          'Be Aware - Changing this tag name will be reflected on all items currently linked to this tag. When editing a tag please consider how this will affect your ability to use this tag moving forward.',
          toastrConfirmOptions
        );
      } else {
        dispatch(updateInventoryTag(formValue));
      }
    }
  };

  const handleCancel = () => {
    setFormValue(tag);
    setFormError({});
    onClose();
  };

  return (
    <Modal size="xs" backdrop="static" show={show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>
          <StyledIcon icon="remind" /> Edit Tag Name
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 15 }}>
        <Form
          model={FormModel}
          ref={formRef}
          formValue={formValue}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          onCheck={handleFormCheck}
          style={{ height: 90 }}
        >
          <FormGroup>
            <ControlLabel>Tag Name</ControlLabel>
            <FormControl
              name="name"
              errorMessage={formError.name}
              style={{ width: 350 }}
            />
          </FormGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance="primary"
          onClick={handleFormSubmit}
          disabled={!allowSubmit || isSaving}
        >
          <Icon icon="check-circle" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button onClick={handleCancel} disabled={isSaving}>
          <Icon icon="close-circle" /> Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

EditTagmodal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tag: PropTypes.shape({}).isRequired
};

export default EditTagmodal;
